import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { login } from "../utils/api";
import { decrypt, encrypt } from "../utils/cryptoUtils";
import { loginSuccess } from "../redux/LoginSlice";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { ToastContainer } from "react-toastify";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [ErrorMsg, SetErrorMsg] = useState("");
  const [password, setPassword] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errors, setErrors] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = { email: "", password: "" };
    let isValid = true;

    if (!email.trim()) {
      newErrors.email = "Email is required.";
      isValid = false;
    } else if (email.length < 3) {
      newErrors.email = "Email must be at least 3 characters.";
      isValid = false;
    }

    if (!password.trim()) {
      newErrors.password = "Password is required.";
      isValid = false;
    } else if (password.length < 8) {
      newErrors.password = "Password must be at least 8 characters.";
      isValid = false;
    } else if (!/[A-Z]/.test(password)) {
      newErrors.password = "Password must contain at least one uppercase letter.";
      isValid = false;
    } else if (!/[a-z]/.test(password)) {
      newErrors.password = "Password must contain at least one lowercase letter.";
      isValid = false;
    } else if (!/[0-9]/.test(password)) {
      newErrors.password = "Password must contain at least one number.";
      isValid = false;
    }

    setErrors(newErrors);
    if (!isValid) return;

    setLoading(true);
    try {
      const jsonData = JSON.stringify({ email, password });
      const encrypted = encrypt(jsonData);
      const response = await login(encrypted);

      if (
        response?.userdatils?.email_verified === 1 ||
        response?.userdatils?.email_verified === 0
      ) {
        dispatch(loginSuccess(response));
        toast.success(response.msg);
        setShowSuccess(true);
        setTimeout(() => {
          if (response.userdatils.email_verified === 1) {
            navigate("/dashboard");
          } else {
            navigate("/Registerotp");
          }
        }, 1500);
      } else {
        SetErrorMsg(response.token);
        setError(response.token || "Unknown error");
        toast.error(response.token || "Unknown error");
      }
    } catch (err) {
      console.error("Login Error:", err.message);
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSignUpClick = () => navigate("/register");
  const handleForgotPassword = () => navigate("/forgetpassword");

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }

        .login-root {
          min-height: 100vh;
          display: flex;
          font-family: 'Inter', sans-serif;
          background: #0f0f1a;
        }

        /* ── LEFT PANEL ── */
        .login-left {
          flex: 1;
          display: none;
          position: relative;
          overflow: hidden;
          background: linear-gradient(145deg, #0d0d1f 0%, #1a0a3d 50%, #0d1a3d 100%);
        }

        @media (min-width: 1024px) {
          .login-left { display: flex; flex-direction: column; justify-content: center; align-items: center; padding: 3rem; }
        }

        /* Animated mesh orbs */
        .login-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.5;
          pointer-events: none;
          animation: orbFloat 12s ease-in-out infinite;
        }
        .login-orb-1 {
          width: 400px; height: 400px;
          background: radial-gradient(circle, rgba(99,91,255,0.6) 0%, transparent 70%);
          top: -100px; left: -100px;
          animation-duration: 15s;
        }
        .login-orb-2 {
          width: 350px; height: 350px;
          background: radial-gradient(circle, rgba(139,92,246,0.5) 0%, transparent 70%);
          bottom: -80px; right: -80px;
          animation-duration: 18s;
          animation-direction: reverse;
        }
        .login-orb-3 {
          width: 250px; height: 250px;
          background: radial-gradient(circle, rgba(59,130,246,0.4) 0%, transparent 70%);
          top: 50%; left: 50%; transform: translate(-50%, -50%);
          animation-duration: 20s;
        }

        @keyframes orbFloat {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -40px) scale(1.08); }
          66% { transform: translate(-20px, 30px) scale(0.95); }
        }

        /* Grid dots overlay */
        .login-grid {
          position: absolute;
          inset: 0;
          background-image: radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px);
          background-size: 28px 28px;
          pointer-events: none;
        }

        /* Left panel content */
        .left-content {
          position: relative;
          z-index: 2;
          text-align: center;
          max-width: 420px;
        }

        .left-brand-icon {
          width: 72px; height: 72px;
          border-radius: 20px;
          background: linear-gradient(135deg, #635bff, #8b5cf6);
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 1.5rem;
          box-shadow: 0 12px 40px rgba(99,91,255,0.6), inset 0 1px 0 rgba(255,255,255,0.2);
          animation: iconGlow 3s ease-in-out infinite;
        }

        @keyframes iconGlow {
          0%, 100% { box-shadow: 0 12px 40px rgba(99,91,255,0.6), inset 0 1px 0 rgba(255,255,255,0.2); }
          50% { box-shadow: 0 16px 55px rgba(99,91,255,0.85), inset 0 1px 0 rgba(255,255,255,0.2); }
        }

        .left-title {
          font-size: 2.5rem;
          font-weight: 800;
          line-height: 1.15;
          letter-spacing: -0.03em;
          background: linear-gradient(135deg, #fff 0%, #c4b5fd 60%, #818cf8 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 1rem;
        }

        .left-subtitle {
          font-size: 0.95rem;
          color: rgba(148,163,184,0.8);
          line-height: 1.65;
          font-weight: 400;
          margin-bottom: 2.5rem;
        }

        /* Feature pills */
        .feature-pills {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          align-items: flex-start;
          width: 100%;
        }

        .feature-pill {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1.25rem;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 1rem;
          width: 100%;
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
        }

        .feature-pill:hover {
          background: rgba(99,91,255,0.1);
          border-color: rgba(99,91,255,0.25);
          transform: translateX(4px);
        }

        .feature-pill-icon {
          width: 36px; height: 36px;
          border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
          font-size: 1.1rem;
        }

        .feature-pill-text strong {
          display: block;
          font-size: 0.82rem;
          font-weight: 600;
          color: #e2e8f0;
          margin-bottom: 1px;
        }
        .feature-pill-text span {
          font-size: 0.72rem;
          color: rgba(148,163,184,0.6);
        }

        /* ── RIGHT PANEL ── */
        .login-right {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem 1.25rem;
          background: linear-gradient(160deg, #0f0f1a 0%, #13111f 100%);
          position: relative;
          overflow: hidden;
        }

        @media (min-width: 1024px) {
          .login-right { width: 480px; flex-shrink: 0; }
        }

        /* Right subtle glow */
        .login-right::before {
          content: '';
          position: absolute;
          top: -60px; right: -60px;
          width: 280px; height: 280px;
          background: radial-gradient(circle, rgba(99,91,255,0.08) 0%, transparent 70%);
          border-radius: 50%;
          pointer-events: none;
        }

        .login-card {
          width: 100%;
          max-width: 400px;
          position: relative;
          z-index: 2;
        }

        /* Top brand for mobile */
        .mobile-brand {
          text-align: center;
          margin-bottom: 2rem;
        }

        @media (min-width: 1024px) {
          .mobile-brand { display: none; }
        }

        .mobile-brand-icon {
          width: 52px; height: 52px;
          border-radius: 14px;
          background: linear-gradient(135deg, #635bff, #8b5cf6);
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 0.75rem;
          box-shadow: 0 8px 24px rgba(99,91,255,0.5);
        }

        /* Heading */
        .login-heading {
          margin-bottom: 0.375rem;
        }

        .login-heading h1 {
          font-size: 1.75rem;
          font-weight: 800;
          color: #f1f5f9;
          letter-spacing: -0.025em;
          line-height: 1.2;
        }

        .login-heading p {
          font-size: 0.875rem;
          color: rgba(148,163,184,0.7);
          margin-top: 0.375rem;
          font-weight: 400;
        }

        /* Divider */
        .login-divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(99,91,255,0.3), transparent);
          margin: 1.5rem 0;
        }

        /* Form field */
        .login-field {
          margin-bottom: 1.25rem;
        }

        .login-field label {
          display: block;
          font-size: 0.78rem;
          font-weight: 600;
          color: rgba(148,163,184,0.8);
          letter-spacing: 0.04em;
          text-transform: uppercase;
          margin-bottom: 0.5rem;
        }

        .login-input-wrap {
          position: relative;
        }

        .login-input-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: rgba(148,163,184,0.4);
          font-size: 1.05rem !important;
          pointer-events: none;
        }

        .login-input {
          width: 100%;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 0.875rem;
          padding: 0.875rem 1rem 0.875rem 2.75rem;
          font-size: 0.9rem;
          color: #f1f5f9;
          font-family: 'Inter', sans-serif;
          transition: all 0.25s ease;
          outline: none;
        }

        .login-input::placeholder { color: rgba(148,163,184,0.35); }

        .login-input:focus {
          border-color: rgba(99,91,255,0.6);
          background: rgba(99,91,255,0.06);
          box-shadow: 0 0 0 3px rgba(99,91,255,0.12);
        }

        .login-input.error {
          border-color: rgba(239,68,68,0.5);
          background: rgba(239,68,68,0.04);
        }

        .login-input.error:focus {
          box-shadow: 0 0 0 3px rgba(239,68,68,0.1);
        }

        .login-eye-btn {
          position: absolute;
          right: 1rem;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          color: rgba(148,163,184,0.45);
          display: flex;
          align-items: center;
          padding: 0;
          transition: color 0.2s;
        }

        .login-eye-btn:hover { color: rgba(148,163,184,0.85); }

        .login-error-msg {
          font-size: 0.72rem;
          color: #f87171;
          margin-top: 0.375rem;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        /* Forgot password row */
        .login-forgot-row {
          display: flex;
          justify-content: flex-end;
          margin-top: -0.5rem;
          margin-bottom: 1.75rem;
        }

        .login-forgot-btn {
          background: none;
          border: none;
          font-size: 0.78rem;
          font-weight: 600;
          color: #818cf8;
          cursor: pointer;
          padding: 0;
          transition: color 0.2s;
          font-family: 'Inter', sans-serif;
        }

        .login-forgot-btn:hover { color: #a78bfa; }

        /* Submit button */
        .login-submit-btn {
          width: 100%;
          padding: 0.9rem 1.5rem;
          background: linear-gradient(135deg, #635bff 0%, #7c3aed 100%);
          border: none;
          border-radius: 0.875rem;
          color: white;
          font-size: 0.9rem;
          font-weight: 700;
          font-family: 'Inter', sans-serif;
          cursor: pointer;
          transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
          box-shadow: 0 8px 24px rgba(99,91,255,0.45), inset 0 1px 0 rgba(255,255,255,0.15);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          letter-spacing: 0.01em;
          position: relative;
          overflow: hidden;
        }

        .login-submit-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%);
          pointer-events: none;
        }

        .login-submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 14px 32px rgba(99,91,255,0.6), inset 0 1px 0 rgba(255,255,255,0.15);
        }

        .login-submit-btn:active:not(:disabled) { transform: translateY(0px); }

        .login-submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        /* Spinner */
        .btn-spinner {
          width: 16px; height: 16px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
        }

        @keyframes spin { to { transform: rotate(360deg); } }

        /* Register link */
        .login-register-row {
          text-align: center;
          margin-top: 1.75rem;
          font-size: 0.82rem;
          color: rgba(148,163,184,0.55);
        }

        .login-register-row button {
          background: none;
          border: none;
          font-size: 0.82rem;
          font-weight: 700;
          color: #818cf8;
          cursor: pointer;
          margin-left: 0.25rem;
          font-family: 'Inter', sans-serif;
          transition: color 0.2s;
        }

        .login-register-row button:hover { color: #a78bfa; }

        /* Security badge */
        .login-security-badge {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.4rem;
          margin-top: 1.5rem;
          font-size: 0.7rem;
          color: rgba(148,163,184,0.35);
          font-weight: 500;
          letter-spacing: 0.03em;
        }
      `}</style>

      <div className="login-root">

        {/* ── LEFT PANEL ── */}
        <div className="login-left">
          <div className="login-grid" />
          <div className="login-orb login-orb-1" />
          <div className="login-orb login-orb-2" />
          <div className="login-orb login-orb-3" />

          <div className="left-content">
            <div className="left-brand-icon">
              <span className="material-icons" style={{ fontSize: "2rem", color: "white" }}>payments</span>
            </div>

            <div className="left-title">Gaadia<br />Trusted Payments</div>

            <p className="left-subtitle">
              A secure, lightning-fast payment gateway built for modern merchants. Manage payouts, monitor funds, and grow your business.
            </p>

            <div className="feature-pills">
              <div className="feature-pill">
                <div className="feature-pill-icon" style={{ background: "rgba(99,91,255,0.2)" }}>
                  <span className="material-icons" style={{ fontSize: "1.15rem", color: "#818cf8" }}>shield</span>
                </div>
                <div className="feature-pill-text">
                  <strong>Bank-Grade Security</strong>
                  <span>256-bit SSL encryption on all transactions</span>
                </div>
              </div>

              <div className="feature-pill">
                <div className="feature-pill-icon" style={{ background: "rgba(16,185,129,0.15)" }}>
                  <span className="material-icons" style={{ fontSize: "1.15rem", color: "#34d399" }}>bolt</span>
                </div>
                <div className="feature-pill-text">
                  <strong>Instant Settlements</strong>
                  <span>Real-time payout processing 24/7</span>
                </div>
              </div>

              <div className="feature-pill">
                <div className="feature-pill-icon" style={{ background: "rgba(245,158,11,0.15)" }}>
                  <span className="material-icons" style={{ fontSize: "1.15rem", color: "#fbbf24" }}>analytics</span>
                </div>
                <div className="feature-pill-text">
                  <strong>Smart Dashboard</strong>
                  <span>Full visibility into your financial operations</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── RIGHT PANEL ── */}
        <div className="login-right">
          <div className="login-card">

            {/* Mobile-only brand */}
            <div className="mobile-brand">
              <div className="mobile-brand-icon">
                <span className="material-icons" style={{ fontSize: "1.5rem", color: "white" }}>payments</span>
              </div>
              <div style={{ fontSize: "1.1rem", fontWeight: 800, color: "#f1f5f9", letterSpacing: "-0.02em" }}>Gaadiaa</div>
              <div style={{ fontSize: "0.7rem", color: "rgba(148,163,184,0.5)", fontWeight: 500, letterSpacing: "0.1em", textTransform: "uppercase", marginTop: "2px" }}>Trusted Payments</div>
            </div>

            {/* Heading */}
            <div className="login-heading">
              <h1>Welcome back</h1>
              <p>Sign in to your merchant account to continue</p>
            </div>

            <div className="login-divider" />

            <form onSubmit={handleSubmit} autoComplete="off">

              {/* Email */}
              <div className="login-field">
                <label>Email Address <span style={{ color: "#f87171" }}>*</span></label>
                <div className="login-input-wrap">
                  <span className="material-icons login-input-icon">mail</span>
                  <input
                    type="text"
                    id="login-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className={`login-input ${errors.email ? "error" : ""}`}
                  />
                </div>
                {errors.email && (
                  <p className="login-error-msg">
                    <span className="material-icons" style={{ fontSize: "0.85rem" }}>error_outline</span>
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Password */}
              <div className="login-field">
                <label>Password <span style={{ color: "#f87171" }}>*</span></label>
                <div className="login-input-wrap">
                  <span className="material-icons login-input-icon">lock</span>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="login-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className={`login-input ${errors.password ? "error" : ""}`}
                    style={{ paddingRight: "3rem" }}
                  />
                  <button
                    type="button"
                    className="login-eye-btn"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
                {errors.password && (
                  <p className="login-error-msg">
                    <span className="material-icons" style={{ fontSize: "0.85rem" }}>error_outline</span>
                    {errors.password}
                  </p>
                )}
              </div>

              {/* Forgot Password */}
              <div className="login-forgot-row">
                <button type="button" className="login-forgot-btn" onClick={handleForgotPassword}>
                  Forgot password?
                </button>
              </div>

              {/* Submit */}
              <button
                type="submit"
                id="login-submit-btn"
                className="login-submit-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="btn-spinner" />
                    Signing in…
                  </>
                ) : (
                  <>
                    Sign In
                    <span className="material-icons" style={{ fontSize: "1.1rem" }}>arrow_forward</span>
                  </>
                )}
              </button>
            </form>

            {/* Register link */}
            <div className="login-register-row">
              New to Gaadiaa?
              <button onClick={handleSignUpClick}>Create account</button>
            </div>

            {/* Security badge */}
            <div className="login-security-badge">
              <span className="material-icons" style={{ fontSize: "0.85rem" }}>lock</span>
              Secured with 256-bit SSL encryption
            </div>

          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        toastStyle={{ background: "#1e1b4b", color: "#e2e8f0", border: "1px solid rgba(99,91,255,0.2)" }}
      />
    </>
  );
};

export default Login;
